import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Eye, Download, Calendar, Clock, Film, Brain, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PoseFrameViewer } from '@/components/pose/PoseFrameViewer';
import { AnalysisListView } from '@/components/pose/AnalysisListView';
import { usePoseAnalysis } from '@/hooks/use-pose-analysis';

interface SwingAnalysis {
  id: string;
  user_id: string;
  video_id: string | null;
  playback_id: string;
  fps: number;
  frame_count: number;
  duration_s: number | null;
  created_at: string;
}

interface SwingFrame {
  frame_index: number;
  time_s: number;
  keypoints: any;
}

export default function PoseAnalysisViewer() {
  const { analysisId } = useParams<{ analysisId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { analyzeFrames, isAnalyzing, analysisResult, clearResult } = usePoseAnalysis();
  
  const [analyses, setAnalyses] = useState<SwingAnalysis[]>([]);
  const [currentAnalysis, setCurrentAnalysis] = useState<SwingAnalysis | null>(null);
  const [frames, setFrames] = useState<SwingFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [framesLoading, setFramesLoading] = useState(false);

  const fetchAnalyses = async () => {
    try {
      const { data, error } = await supabase
        .from('swing_analyses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnalyses(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load pose analyses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysisFrames = async (analysisIdParam: string) => {
    setFramesLoading(true);
    try {
      const { data: analysisData, error: analysisError } = await supabase
        .from('swing_analyses')
        .select('*')
        .eq('id', analysisIdParam)
        .single();

      if (analysisError) throw analysisError;
      setCurrentAnalysis(analysisData);

      const { data: framesData, error: framesError } = await supabase
        .from('swing_frames')
        .select('*')
        .eq('analysis_id', analysisIdParam)
        .order('frame_index');

      if (framesError) throw framesError;
      setFrames(framesData || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load analysis details",
        variant: "destructive"
      });
    } finally {
      setFramesLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyses();
  }, []);

  useEffect(() => {
    if (analysisId) {
      fetchAnalysisFrames(analysisId);
      clearResult(); // Clear previous AI analysis when viewing new analysis
    }
  }, [analysisId, clearResult]);

  const downloadAnalysis = () => {
    if (!currentAnalysis || !frames.length) return;
    
    const data = {
      analysis: currentAnalysis,
      frames: frames
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pose-analysis-${currentAnalysis.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading analyses...</div>
        </div>
      </div>
    );
  }

  // Show detailed view if analysisId is provided
  if (analysisId && currentAnalysis) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/pose-analysis')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Analyses
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                Pose Analysis Details
              </h1>
              <p className="text-muted-foreground">
                Analysis ID: {currentAnalysis.id.slice(0, 8)}...
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => analyzeFrames(currentAnalysis.id)} 
              disabled={isAnalyzing || frames.length === 0}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isAnalyzing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}
            </Button>
            <Button onClick={downloadAnalysis} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>
        </div>

        {/* Analysis Metadata */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Film className="w-5 h-5" />
              Analysis Metadata
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Date</div>
                  <div className="font-medium">
                    {new Date(currentAnalysis.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Duration</div>
                  <div className="font-medium">
                    {currentAnalysis.duration_s ? `${currentAnalysis.duration_s.toFixed(1)}s` : 'N/A'}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Frame Rate</div>
                <div className="font-medium">{currentAnalysis.fps} FPS</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Frames</div>
                <div className="font-medium">{frames.length} captured</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Analysis Results */}
        {analysisResult && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                AI Golf Swing Analysis
                <Badge variant="secondary" className="ml-2">
                  {analysisResult.frames_analyzed} frames analyzed
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="whitespace-pre-wrap text-foreground leading-relaxed">
                    {analysisResult.analysis}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-3 text-center">
                  Powered by No Doubles AI Analysis
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pose Frame Viewer */}
        {framesLoading ? (
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-muted-foreground">
                Loading frame data...
              </div>
            </CardContent>
          </Card>
        ) : frames.length > 0 ? (
          <PoseFrameViewer frames={frames} />
        ) : (
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-muted-foreground">
                No frame data available for this analysis
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Show list view
  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          Pose Analysis History
        </h1>
        <p className="text-muted-foreground">
          View and analyze your captured golf swing pose data
        </p>
      </div>

      <AnalysisListView analyses={analyses} />
    </div>
  );
}